import React, { useState, useEffect, useCallback } from 'react';
import { Table, Select, Row, Col, Statistic, Card, Empty, Spin } from 'antd';
import { FileDoneOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import TableExportButton from '../../components/TableExportButton';
import PageHeader from '../../components/PageHeader';
import { PAYS_CEDEAO } from '../../variables/paysCedeao';

import { localeIntl } from '../../i18n/format';

const fmt = (v) => (v == null ? '0' : Number(v).toLocaleString(localeIntl()));

export default function EtatBapExercices() {
  const { t } = useTranslation('statistiques');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [codePays, setCodePays] = useState(null);
  const [exercice, setExercice] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (codePays) params.set('codePays', codePays);
    if (exercice) params.set('exercice', exercice);
    axiosInstance.get(`/v1/stats/bap-exercices?${params.toString()}`)
      .then((r) => setData(r.data?.data))
      .catch(() => toast.error(t('bap.chargementImpossible')))
      .finally(() => setLoading(false));
  }, [codePays, exercice, t]);

  useEffect(() => { load(); }, [load]);

  const exercicesDispo = data
    ? [...new Set(data.lignes.map((l) => l.exercice).filter(Boolean))].sort((a, b) => b - a)
    : [];

  const cols = [
    { title: t('bap.colonnes.exercice'), dataIndex: 'exercice', width: 90, align: 'center',
      sorter: (a, b) => (a.exercice || 0) - (b.exercice || 0), defaultSortOrder: 'descend',
      render: (v) => v || '-' },
    { title: t('bap.colonnes.pays'), dataIndex: 'paysLibelle', width: 130, export: (v) => v || '' },
    { title: t('bap.colonnes.compagnieBureau'), dataIndex: 'compagnie', ellipsis: true, export: (v) => v || '' },
    { title: t('bap.nbBap'), dataIndex: 'nbBap', width: 90, align: 'center' },
    { title: t('bap.colonnes.montantBap'), dataIndex: 'montantBapTotal', width: 140, align: 'right',
      render: fmt, export: (v) => v },
    { title: t('bap.colonnes.nbPayes'), dataIndex: 'nbBapPaye', width: 90, align: 'center' },
    { title: t('bap.montantPaye'), dataIndex: 'montantBapPaye', width: 140, align: 'right',
      render: (v) => <span style={{ color: '#1a6b3c' }}>{fmt(v)}</span>, export: (v) => v },
    { title: t('bap.colonnes.restant'), dataIndex: 'montantBapRestant', width: 140, align: 'right',
      render: (v) => <span style={{ color: v > 0 ? '#d97706' : undefined }}>{fmt(v)}</span>,
      export: (v) => v },
  ];

  const tot = data?.total;

  return (
    <Layout title={t('bap.titreOnglet')}>
      {/* `data.lignes` est la source du tableau ; `data.total` est une synthese
          affichee a part, elle ne compte pas comme une ligne. */}
      <PageHeader
        titre={t('bap.titre')}
        icone={<FileDoneOutlined />}
        compte={data ? (data.lignes || []).length : undefined}
        unite={t('bap.unite')}
      />

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col>
            <Select allowClear placeholder={t('bap.tousPays')} style={{ width: 200 }}
              value={codePays} onChange={setCodePays}
              options={PAYS_CEDEAO.map((p) => ({ value: p.code, label: `${p.flag} ${p.nom}` }))} />
          </Col>
          <Col>
            <Select allowClear placeholder={t('bap.tousExercices')} style={{ width: 180 }}
              value={exercice} onChange={setExercice}
              options={exercicesDispo.map((e) => ({ value: e, label: String(e) }))} />
          </Col>
        </Row>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
      ) : !data || !data.lignes.length ? (
        <Empty description={t('bap.aucunBap')} style={{ padding: 60 }} />
      ) : (
        <>
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={5}><Statistic title={t('bap.nbBap')} value={tot.nbBap} /></Col>
              <Col span={6}><Statistic title={t('bap.montantBapTotal')} value={fmt(tot.montantBapTotal)} /></Col>
              <Col span={6}><Statistic title={t('bap.montantPaye')} value={fmt(tot.montantBapPaye)}
                valueStyle={{ color: '#1a6b3c' }} suffix={`(${tot.nbBapPaye})`} /></Col>
              <Col span={6}><Statistic title={t('bap.restantAPayer')} value={fmt(tot.montantBapRestant)}
                valueStyle={{ color: tot.montantBapRestant > 0 ? '#d97706' : undefined }} /></Col>
            </Row>
          </Card>

          <Card>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <TableExportButton
                floating data={data.lignes} columns={cols} filename="bap-par-exercice"
                title={t('bap.titre')} count={data.lignes.length} />
            </div>
            <Table size="small" rowKey={(r, i) => i} columns={cols} dataSource={data.lignes}
              pagination={{ pageSize: 25, showSizeChanger: true }} />
          </Card>
        </>
      )}
    </Layout>
  );
}
